import AddBlacklistDialog from 'features/co-pilot/company-blacklists/add-blacklist-dialog';
import { useAddBlacklist } from 'features/co-pilot/company-blacklists/use-add-blacklist';
import { useLinkedinAccountsQuery } from 'features/co-pilot/linkedin-accounts/hooks';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useErrorStore from 'stores/error-store';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Nach dem Speichern, wenn der Dialog schon zu ist. `id` ist die neue Blacklist (falls geliefert). */
  onCreated: (id: string | undefined, name: string, type: string) => void | Promise<void>;
}

/**
 * „Neue Blacklist anlegen“ in Schritt 2: DIESELBE Anlage-Maske wie in der Blacklist-Verwaltung
 * (`AddBlacklistDialog` + `useAddBlacklist`), nur als Fenster im Fenster. Der Wizard-Zustand
 * bleibt unberührt, weil nichts navigiert oder neu geladen wird.
 */
const NewBlacklistDialog: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const { t, i18n } = useTranslation();
  const handleOpenErrorDialog = useErrorStore(state => state.handleOpenErrorDialog);
  const { data: linkedinAccounts = [] } = useLinkedinAccountsQuery();

  const blacklist = useAddBlacklist({
    linkedinAccounts,
    sprache: i18n.languages[0],
    onCreated: (name, type, _count, id) => onCreated(id, name, type),
    onError: error => handleOpenErrorDialog(error as never, '/blacklists', 'post'),
    // Kein Wegnavigieren: der Wizard-Zustand soll erhalten bleiben.
    onNeedsRelogin: () =>
      window.alert(
        t(
          'Your LinkedIn account session is not active. Do you want to open LinkedIn Account Management and login again?'
        )
      ),
  });
  const { open: openDialog, close: closeDialog, setState } = blacklist;

  useEffect(() => {
    if (open) openDialog();
    else closeDialog();
    // Nur auf das Öffnen/Schließen von außen reagieren.
  }, [open]);

  useEffect(() => {
    const defaultAccountId = linkedinAccounts[0]?._id ?? '';
    setState(prev => ({
      ...prev,
      selectedLinkedinAccount: prev.selectedLinkedinAccount || defaultAccountId,
    }));
  }, [linkedinAccounts, setState]);

  if (!blacklist.isOpen) return null;

  return (
    <AddBlacklistDialog
      {...blacklist.dialogProps}
      variant="wizard"
      handleClose={() => {
        blacklist.close();
        onClose();
      }}
      handleCreateGlobalBlacklist={blacklist.dialogProps.handleCreate}
      // Hier wird nur angelegt; Bearbeiten bleibt der Blacklist-Verwaltung vorbehalten.
      handleUpdateBlacklistItems={() => undefined}
    />
  );
};

export default NewBlacklistDialog;
